'use client';

import { HTMLAttributes, forwardRef, ReactNode, useEffect, useRef, useCallback } from 'react';
import Backdrop from '../primitives/Backdrop';
import CloseButton from '../primitives/CloseButton';
import { lockBodyScroll, unlockBodyScroll } from '../utils/scrollLock';
import styles from './Modal.module.css';

type ModalSize = 'sm' | 'md' | 'lg' | 'full';

interface ModalProps extends HTMLAttributes<HTMLDivElement> {
  open: boolean;
  onClose: () => void;
  size?: ModalSize;
  title?: string;
  /** Optional supporting line under the title, inside the header (above the divider). */
  description?: ReactNode;
  icon?: ReactNode;
  children: ReactNode;
  /**
   * Row pinned between the header and the scrolling body — a search field, a
   * filter bar, tabs. It stays put while the body scrolls under it.
   */
  toolbar?: ReactNode;
  footer?: ReactNode;
  closeOnBackdrop?: boolean;
  showCloseButton?: boolean;
  /**
   * Focus this element when the modal opens, instead of the first focusable one
   * (which is usually the header's ✕). Use it when the dialog opens on a field the
   * user is expected to type into straight away.
   */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  /**
   * Restore focus to the element that was focused before the modal opened, on close.
   * Defaults to true. Set false when the host manages focus itself (e.g. a composer
   * that must return the caret to its editor rather than the trigger button).
   */
  restoreFocus?: boolean;
}

const Modal = forwardRef<HTMLDivElement, ModalProps>(({
  open,
  onClose,
  size = 'md',
  title,
  description,
  icon,
  children,
  toolbar,
  footer,
  closeOnBackdrop = true,
  showCloseButton = true,
  initialFocusRef,
  restoreFocus = true,
  className = '',
  ...props
}, ref) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  // Keep the latest onClose in a ref so the keydown handler stays stable — otherwise
  // an inline onClose (new identity each render) would recreate the handler and thrash
  // the focus effect, re-capturing/re-stealing focus on every parent render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCloseRef.current();
      return;
    }
    if (e.key === 'Tab' && contentRef.current) {
      const focusable = contentRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      // literal index (D9 class 2): the early return above guarantees focusable is non-empty.
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  // Body scroll lock — ref-counted so closed overlays can't clobber it.
  useEffect(() => {
    if (!open) return;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [open]);

  // Focus management runs only on open⇄close transitions (deps are stable), so a
  // re-rendering parent can't re-capture or re-steal focus while the modal is open.
  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement as HTMLElement;
    document.addEventListener('keydown', handleKeyDown);
    const focusTimer = setTimeout(() => {
      const requested = initialFocusRef?.current;
      if (requested) { requested.focus({ preventScroll: true }); return; }
      const focusable = contentRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable && focusable.length > 0) focusable[0]!.focus({ preventScroll: true });
    }, 50);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearTimeout(focusTimer);
      // Restore focus on close/unmount unless the host opts out to manage it itself.
      if (restoreFocus) previousFocusRef.current?.focus({ preventScroll: true });
    };
  }, [open, restoreFocus, handleKeyDown, initialFocusRef]);

  if (!open) return null;

  return (
    <div
      className={`${styles.overlay} ${className}`}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      ref={ref}
      {...props}
    >
      <Backdrop className={styles.backdrop} onClick={closeOnBackdrop ? onClose : undefined} />
      <div className={`${styles.content} ${styles[size]}`} ref={contentRef}>
        {(title || description || showCloseButton) && (
          <div className={[styles.header, description ? styles.hasDescription : ''].filter(Boolean).join(' ')}>
            <div className={styles.headerLeft}>
              {icon && <span className={styles.headerIcon}>{icon}</span>}
              {(title || description) && (
                <div className={styles.headerText}>
                  {title && <h2 className={styles.title}>{title}</h2>}
                  {description && <p className={styles.description}>{description}</p>}
                </div>
              )}
            </div>
            {showCloseButton && (
              <CloseButton className={styles.closeButton} onClick={onClose} aria-label="Close modal" size="lg" />
            )}
          </div>
        )}
        {toolbar && <div className={styles.toolbar}>{toolbar}</div>}
        <div className={styles.body}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>
  );
});

Modal.displayName = 'Modal';
export default Modal;
